import React from "react";
import { ReactComponent as EyeIcon } from "assets/icons/eye.svg";
import { ReactComponent as BinIcon } from "assets/icons/bin.svg";
import { ReactComponent as ChevronIcon } from "assets/icons/chevron.svg";
import { SortState } from "./useTableView";

/**
 * Column definition object: keys are columns ids and values columns headers.
 */
type Columns = { [key: string]: string };

/**
 * Each row object must conform to column definition object, meaning that each
 * value that its key is not found in columns definition object will be ignored.
 * Also, each row must have an id of string.
 */
type Row = { id: string } & { [key: keyof Columns]: string | number | Date };

interface Props<T> {
  columns: Columns;
  rows: Array<T>;
  renderCell?: (colId: string, row: T) => React.ReactNode;
  handleViewItem?: (id: string) => void;
  handleRemoveItem?: (id: string) => void;
  removeItemLabel?: string;
  /** Current sort, from useTableView. Omit to leave the table unsorted. */
  sort?: SortState;
  /** Called with the column id when a header is clicked. Omit to disable sorting. */
  onSortChange?: (colId: string) => void;
  /** Shown in place of rows when there are none. */
  emptyMessage?: string;
  /**
   * Rows are on their way.
   *
   * With no rows yet this shows placeholders; with rows already showing it
   * leaves them alone. Blanking a table on every page change is worse than a
   * briefly stale list, and showing the empty message first tells a clinic
   * "no clients yet" a beat before showing them their four hundred clients.
   */
  loading?: boolean;
}

/**
 * Enough placeholder rows to hold the table's height while it loads. A table
 * that collapses to nothing and springs back moves everything below it twice.
 */
const SKELETON_ROWS = [0, 1, 2, 3, 4];

function Table<TCell extends Row>(props: Props<TCell>) {
  const onEyeButtonClicked = (id: string) => {
    props.handleViewItem?.(id);
  };

  const onBinButtonClicked = (id: string) => {
    props.handleRemoveItem?.(id);
  };

  const sortable = Boolean(props.onSortChange);
  const columnIds = Object.keys(props.columns);
  const hasActions = Boolean(props.handleViewItem || props.handleRemoveItem);
  const columnCount = columnIds.length + (hasActions ? 1 : 0);

  // overflow-x-auto, not overflow-hidden: a wide table must scroll on a narrow
  // viewport. Hidden clipped the right-hand columns out of reach entirely.
  return (
    <div className="w-full overflow-x-auto rounded-[10px] border border-slate-200 bg-white">
      <table className="w-full text-left text-sm" aria-busy={props.loading ? "true" : "false"}>
        <thead className="bg-slate-50/70 border-b border-slate-200">
          <tr>
            {Object.entries(props.columns).map((col) => {
              const colId = col[0];
              const isSorted = props.sort?.colId === colId;
              const dir = isSorted ? props.sort?.dir : undefined;

              return (
                <th
                  className="whitespace-nowrap px-5 py-3 text-[0.7rem] font-semibold uppercase tracking-[0.05em] text-slate-500"
                  key={colId}
                  aria-sort={
                    isSorted
                      ? dir === "asc"
                        ? "ascending"
                        : "descending"
                      : undefined
                  }
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => props.onSortChange?.(colId)}
                      className="inline-flex items-center uppercase tracking-[0.05em] font-semibold hover:text-slate-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-vg-blue/30 rounded"
                    >
                      <span>{col[1]}</span>
                      <ChevronIcon
                        className={[
                          "inline-block ml-1.5 align-middle transition-transform",
                          isSorted ? "opacity-80" : "opacity-30",
                          dir === "asc" ? "rotate-180" : "",
                        ].join(" ")}
                      />
                    </button>
                  ) : (
                    <>
                      <span>{col[1]}</span>
                      <ChevronIcon className="inline-block ml-1.5 opacity-40 align-middle" />
                    </>
                  )}
                </th>
              );
            })}
            {(props.handleViewItem || props.handleRemoveItem) && (
              <th className="px-5 py-3 text-[0.7rem] font-semibold uppercase tracking-[0.05em] text-slate-500 w-[1%] whitespace-nowrap text-right">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="text-slate-700">
          {props.loading && props.rows.length === 0 &&
            SKELETON_ROWS.map((key) => (
              <tr key={key} data-testid="table-skeleton-row" className="animate-pulse">
                {Object.keys(props.columns).map((colId) => (
                  <td key={colId} className="px-5 py-4">
                    <div className="h-3 rounded bg-slate-200" />
                  </td>
                ))}
                {(props.handleViewItem || props.handleRemoveItem) && (
                  <td className="px-5 py-4">
                    <div className="h-3 rounded bg-slate-200" />
                  </td>
                )}
              </tr>
            ))}
          {!props.loading && props.rows.length === 0 && (
            <tr>
              <td
                colSpan={columnCount}
                className="px-5 py-10 text-center text-sm text-slate-500"
              >
                {props.emptyMessage ?? "Nothing to show yet."}
              </td>
            </tr>
          )}
          {props.rows.map((row, idx) => (
            <tr
              key={idx}
              className="border-b border-slate-100 last:border-b-0 transition-colors hover:bg-slate-50/60"
            >
              {Object.keys(props.columns).map((colId) => {
                const custom = props.renderCell?.(colId, row);
                return (
                  <td key={colId} className="px-5 py-3 align-middle">
                    {custom !== undefined ? custom : row[colId]?.toString()}
                  </td>
                );
              })}

              {(props.handleViewItem || props.handleRemoveItem) && (
                <td className="px-5 py-3 align-middle whitespace-nowrap">
                  <div className="flex items-center justify-end gap-1.5">
                    {props.handleViewItem && (
                      <button
                        type="button"
                        onClick={() => onEyeButtonClicked(row.id)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-vg-blue/10 text-vg-blue hover:bg-vg-blue/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-vg-blue/30"
                      >
                        <EyeIcon className="h-3.5 w-3.5 pointer-events-none" />
                        View
                      </button>
                    )}
                    {props.handleRemoveItem && (
                      <button
                        type="button"
                        onClick={() => onBinButtonClicked(row.id)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-vg-cherry/10 text-vg-cherry hover:bg-vg-cherry/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-vg-cherry/30"
                      >
                        <BinIcon className="h-3.5 w-3.5 pointer-events-none" />
                        {props.removeItemLabel ?? "Delete"}
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
